/**
 * BrandMark — the logo mark drawn inline, for a "0" inside a word whose ground is not known in advance (the pr0
 * currency sign sits on white cards, dark chips and gold buttons alike).
 *
 * Its ink arc is `currentColor`, so it takes the ink of the words around it; the gold arc is the day's satin, or the
 * dusk satin where the ground is the new design's dusk (`dusk`: the workspace switch at dusk, PV5-8:D, 2026-10-13, as
 * the boot splash's zero shows it). It is drawn on the old raster's own 545 x 512 canvas with the ring where the old
 * orb was (scripts/lib/brand-icon-svgs.mjs, DROP_IN_ORB), so the sizes and offsets every placement was tuned with for
 * that image still hold, and it sits exactly where the boot splash's zero sits.
 *
 * A zero inside a word carries `label="0"` (read as the digit, as the image it replaces was).
 */
import React from 'react';
import { semanticDark, semanticLight } from '../../styles/semanticTokens.generated';
import { MARK_GOLD_ARC, MARK_GOLD_Y1, MARK_GOLD_Y2, MARK_INK_ARC, MARK_STROKE, satinStops } from '../../design/pavilion/markGeometry';

/** Kept equal to dropInTransform() in scripts/lib/brand-icon-svgs.mjs by scripts/__tests__/one-mark.test.mjs. */
export const BRAND_MARK_DROP_IN_TRANSFORM = 'translate(276 259.5) scale(4.7159) translate(-50 -50)';

export const BrandMark: React.FC<{ className?: string; label?: string; dusk?: boolean }> = ({ className, label, dusk = false }) => {
  const raw = React.useId();
  const id = `brand-mark-${raw.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const };
  return (
    <svg className={className} viewBox="0 0 545 512" width="545" height="512" focusable="false" data-brand-mark="" {...a11y}>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1={MARK_GOLD_Y1} x2="0" y2={MARK_GOLD_Y2}>
          {satinStops(dusk ? semanticDark : semanticLight).map(([offset, color]) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
      </defs>
      <g transform={BRAND_MARK_DROP_IN_TRANSFORM}>
        <path d={MARK_INK_ARC} fill="none" stroke="currentColor" strokeWidth={MARK_STROKE} strokeLinecap="round" />
        <path d={MARK_GOLD_ARC} fill="none" stroke={`url(#${id})`} strokeWidth={MARK_STROKE} strokeLinecap="round" />
      </g>
    </svg>
  );
};

export default BrandMark;
